import { useAtomValue } from "jotai";
import { mergeClass } from "../../utils";
import { Button } from "../Button";
import { DisplayValue } from "../DisplayValue";
import _ from "lodash";
import { usePublisher } from "./usePublisher";
import { publishGroups } from "./publishConfig";
import { useState } from "react";
import { Spinner } from "../Loader";
import { hostDataAtom } from "../../atoms";
import { getGroupDataFromModel } from "./utils";
import { Banner } from "./PublishBanner";

interface IPublishBannerText {
  published?: string;
  unpublished?: string;
}

const IPublishBannerDefaults: IPublishBannerText = {
  published: "Published content is up to date",
  unpublished: "There are unpublished changes",
};

export const PublishBanner = ({
  appGroup,
}: {
  appGroup: keyof typeof publishGroups;
}): JSX.Element => {
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const publishState = useAtomValue(publishGroups[appGroup].atom);
  const upToDate = publishState.lastchange === publishState.lastpublish;
  const publish = usePublisher(appGroup, setIsPublishing);
  const hostData = useAtomValue(hostDataAtom);
  const baseUrl = `https://${hostData.aws_bucket}.s3.${hostData.aws_region}.amazonaws.com/endpoints/`;
  const endpointUrl = `${baseUrl}${appGroup}.json`;
  return (
    <Banner
      published={upToDate}
      isPublishing={isPublishing}
      handlePublishPressed={() => publish()}
      endpointUrl={endpointUrl}
    />
  );
};

export const PublishBannerByModel = ({ modelName }: { modelName: string }) => (
  <PublishBanner
    appGroup={
      getGroupDataFromModel(modelName).group as keyof typeof publishGroups
    }
  />
);

const IndexPublishSection = ({
  appGroup,
  publishText = IPublishBannerDefaults,
}: {
  appGroup: keyof typeof publishGroups;
  publishText?: IPublishBannerText;
}): JSX.Element => {
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const publishState = useAtomValue(publishGroups[appGroup].atom);
  const upToDate = publishState.lastchange === publishState.lastpublish;
  const publish = usePublisher(appGroup, setIsPublishing);
  return (
    <div
      className={mergeClass(
        "w-full px-4 py-2 mb-3 font-medium text-sm rounded-sm text-white flex justify-between",
        {
          "bg-grayBg": !upToDate,
          "bg-successGreen": upToDate,
        }
      )}
    >
      <div className="place-self-center">
        {upToDate ? publishText.published : publishText.unpublished}
      </div>
      <Button
        className={mergeClass("", {
          "bg-successGreen border-successGreen": !upToDate,
          "bg-grayBg border-grey-500": upToDate,
        })}
        disabled={isPublishing}
        onClick={() => publish()}
      >
        <div className="flex gap-2">
          Publish changes {isPublishing && <Spinner />}
        </div>
      </Button>
    </div>
  );
};

const HomePublishSection = ({
  appGroup,
}: {
  appGroup: keyof typeof publishGroups;
}): JSX.Element => {
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const publishState = useAtomValue(publishGroups[appGroup].atom);
  const upToDate = publishState.lastchange === publishState.lastpublish;
  const publish = usePublisher(appGroup, setIsPublishing);
  return (
    <div className="mt-2 text-sm flex flex-col">
      <DisplayValue name="Last Modified" value={publishState.lastchange} />
      <DisplayValue
        name="Last Published"
        value={publishState.lastpublish}
        className={mergeClass("flex-grow", {
          "text-unpublishedHome font-bold": upToDate,
          "text-primaryColor": !upToDate,
        })}
      />
      <div>
        <Button
          className="text-sm mt-2 flex gap-2"
          disabled={isPublishing}
          onClick={() => publish()}
        >
          Push Changes {isPublishing && <Spinner />}
        </Button>
      </div>
    </div>
  );
};

export { HomePublishSection, IndexPublishSection };
